import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Loader2, Eye, EyeOff, Mail } from 'lucide-react';
import { BrandLogo, BrandLogoDark } from '@/components/BrandLogo';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      const user = data.user;
      if (user.subscription_status !== 'active') {
        toast.info('Please choose a plan to get started.');
        navigate('/pricing');
      } else {
        toast.success('Welcome back!');
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error('Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail || !resetEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setSendingReset(true);
    try {
      await api.post('/auth/forgot-password', { email: resetEmail });
      toast.success('Password reset link sent to your email');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setSendingReset(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Brand */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-primary/5">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1518031571452-5f37a6d8fb62?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85)', filter: 'brightness(0.4)' }} />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <button type="button" onClick={() => navigate('/')} data-testid="login-logo-button" aria-label="Back to homepage" className="flex items-center gap-3 cursor-pointer bg-transparent border-0 p-0">
            <BrandLogoDark width={200} />
          </button>
          <div>
            <h1 className="text-4xl font-bold leading-tight mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Vendor compliance,<br />automated.</h1>
            <p className="text-lg text-white/70 max-w-md">AI-powered document extraction,<br />real-time compliance scoring,<br />and automated vendor chasing for property managers.</p>
          </div>
        </div>
      </div>

      {/* Right - Login form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <Card className="w-full max-w-[400px] shadow-[var(--shadow-soft)]">
          <CardHeader className="space-y-1 text-center">
            <button type="button" onClick={() => navigate('/')} data-testid="login-logo-button-mobile" aria-label="Back to homepage" className="flex justify-center mb-4 lg:hidden mx-auto cursor-pointer bg-transparent border-0 p-0">
              <BrandLogo width={180} />
            </button>
            <CardTitle className="text-2xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Welcome back</CardTitle>
            <CardDescription>Sign in to your account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" data-testid="login-email-input" placeholder="admin@apexpm.com" value={email} onChange={e => setEmail(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <button type="button" onClick={() => setShowForgotPassword(true)} data-testid="login-forgot-password-link" className="text-xs text-primary hover:underline">Forgot password?</button>
                </div>
                <div className="relative">
                  <Input id="password" type={showPassword ? "text" : "password"} data-testid="login-password-input" value={password} onChange={e => setPassword(e.target.value)} required className="pr-10" />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" data-testid="login-submit-button" className="w-full" disabled={loading}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Don't have an account? <Link to="/signup" className="text-primary hover:underline">Sign up</Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email">Email</Label>
              <Input
                id="reset-email"
                type="email"
                placeholder="you@company.com"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                data-testid="forgot-password-email-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForgotPassword(false)}>
              Cancel
            </Button>
            <Button onClick={handleForgotPassword} disabled={sendingReset} data-testid="forgot-password-submit">
              {sendingReset ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
              {sendingReset ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
